// utils/debugLogger.js — CAPTEUR DE LOGS EMBARQUÉ (surtout pour l'app Android).
//
// But (demande Lucas, juillet 2026) : « un outil qui permet de voir les logs
// que je puisse te les copier, et ajoute le plus de logs possible ». On capture
// TOUT côté client, dans un buffer qui SURVIT à un reload/crash (miroir
// localStorage), lisible et copiable via le bouton 🐞 (composant DebugConsole).
//
// S'installe à l'import, AVANT React, pour attraper les erreurs de boot. Zéro
// dépendance. Capture : console.*, erreurs JS, rejets de promesses, requêtes
// fetch ET XHR (donc axios), navigation, online/offline, cycle de vie.
//
// Aucune donnée n'est envoyée nulle part : tout reste sur l'appareil jusqu'à ce
// que l'utilisateur copie le rapport et le colle lui-même.

const MAX = 600;
const LS_KEY = "gively_debug_log_v1";

let buf = [];
let listeners = [];
let installed = false;

// Restaure les logs de la session précédente (utile après un crash/reload).
try {
  const raw = localStorage.getItem(LS_KEY);
  if (raw) buf = JSON.parse(raw).slice(-MAX);
} catch (_) { /* storage indispo */ }

function _time() {
  try { return new Date().toISOString().slice(11, 23); } catch (_) { return "??:??:??"; }
}
function _safe(o) {
  if (typeof o === "string") return o;
  try {
    return JSON.stringify(o, (k, v) => (v instanceof Error ? { message: v.message, stack: v.stack } : v));
  } catch (_) {
    try { return String(o); } catch (__) { return "[unserializable]"; }
  }
}
function _persist() {
  try { localStorage.setItem(LS_KEY, JSON.stringify(buf.slice(-MAX))); } catch (_) { /* quota */ }
}

export function pushLog(level, cat, msg) {
  const entry = { t: _time(), level, cat, msg: typeof msg === "string" ? msg : _safe(msg) };
  buf.push(entry);
  if (buf.length > MAX) buf.splice(0, buf.length - MAX);
  _persist();
  for (const fn of listeners) { try { fn(); } catch (_) { /* noop */ } }
  return entry;
}
export function getLogs() { return buf.slice(); }
export function clearLogs() { buf = []; _persist(); for (const fn of listeners) { try { fn(); } catch (_) {} } }
export function subscribe(fn) { listeners.push(fn); return () => { listeners = listeners.filter((x) => x !== fn); }; }

export function envSnapshot() {
  const w = typeof window !== "undefined" ? window : {};
  const cap = w.Capacitor;
  let isNative = false, platform = "web";
  try { isNative = !!(cap && cap.isNativePlatform && cap.isNativePlatform()); } catch (_) {}
  try { platform = (cap && cap.getPlatform && cap.getPlatform()) || "web"; } catch (_) {}
  return {
    origin: (w.location && w.location.origin) || "?",
    href: (w.location && w.location.href) || "?",
    isNative,
    platform,
    capacitor: !!cap,
    online: typeof navigator !== "undefined" ? navigator.onLine : "?",
    userAgent: (typeof navigator !== "undefined" && navigator.userAgent) || "?",
    language: (typeof navigator !== "undefined" && navigator.language) || "?",
    cookiesPresent: (typeof document !== "undefined" ? (document.cookie || "").split(";").filter(Boolean).length : 0),
    swControlled: !!(typeof navigator !== "undefined" && navigator.serviceWorker && navigator.serviceWorker.controller),
    buildId: w.__GIVELY_BUILD_ID__ || "?",
  };
}

export function installDebugLogger() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // 1) console.* — on garde le comportement d'origine ET on capture.
  ["log", "info", "warn", "error"].forEach((m) => {
    const orig = (console[m] && console[m].bind(console)) || function () {};
    console[m] = function (...args) {
      try { pushLog(m === "log" ? "info" : m, "console", args.map((a) => _safe(a)).join(" ")); } catch (_) {}
      try { orig(...args); } catch (_) {}
    };
  });

  // 2) Erreurs JS non capturées + rejets de promesses.
  window.addEventListener("error", (e) => {
    try {
      const stack = e && e.error && e.error.stack ? "\n" + e.error.stack : "";
      pushLog("error", "js", `${(e && (e.message || (e.error && e.error.message))) || "error"} @ ${(e && e.filename) || ""}:${(e && e.lineno) || ""}${stack}`);
    } catch (_) {}
  });
  window.addEventListener("unhandledrejection", (e) => {
    try {
      const r = e && e.reason;
      pushLog("error", "promise", `${(r && (r.message || r)) || "rejet"}${r && r.stack ? "\n" + r.stack : ""}`);
    } catch (_) {}
  });

  // 3) fetch.
  if (window.fetch) {
    const of = window.fetch.bind(window);
    window.fetch = async function (input, init) {
      const url = typeof input === "string" ? input : (input && input.url) || "";
      const method = (init && init.method) || (input && input.method) || "GET";
      const t0 = Date.now();
      try {
        const res = await of(input, init);
        pushLog(res.ok ? "info" : "warn", "fetch", `${method} ${url} → ${res.status} (${Date.now() - t0}ms)`);
        return res;
      } catch (err) {
        pushLog("error", "fetch", `${method} ${url} → ÉCHEC ${(err && err.message) || err} (${Date.now() - t0}ms)`);
        throw err;
      }
    };
  }

  // 4) XMLHttpRequest — c'est ce qu'utilise axios (toutes les requêtes API).
  const XHR = window.XMLHttpRequest;
  if (XHR && XHR.prototype) {
    const open = XHR.prototype.open;
    const send = XHR.prototype.send;
    XHR.prototype.open = function (method, url) { this.__g = { method, url, t0: Date.now() }; return open.apply(this, arguments); };
    XHR.prototype.send = function () {
      const g = this.__g || {};
      this.addEventListener("loadend", () => {
        try {
          const ms = Date.now() - (g.t0 || Date.now());
          const st = this.status;
          const ok = st >= 200 && st < 400;
          pushLog(ok ? "info" : "error", "api", `${g.method || "?"} ${g.url || "?"} → ${st || "0 (réseau/CORS)"} (${ms}ms)`);
        } catch (_) {}
      });
      return send.apply(this, arguments);
    };
  }

  // 5) Navigation SPA (React Router pousse via history).
  try {
    const push = window.history.pushState;
    window.history.pushState = function () { try { pushLog("info", "nav", `→ ${arguments[2] || ""}`); } catch (_) {} return push.apply(this, arguments); };
    window.addEventListener("popstate", () => { try { pushLog("info", "nav", `← ${window.location.pathname}`); } catch (_) {} });
  } catch (_) {}

  // 6) Réseau on/off.
  window.addEventListener("online", () => pushLog("info", "net", "connexion rétablie"));
  window.addEventListener("offline", () => pushLog("warn", "net", "hors ligne"));

  // 7) Visibilité (proxy grossier du cycle de vie resume/pause en WebView).
  document.addEventListener("visibilitychange", () => pushLog("info", "vie", `visibilité: ${document.visibilityState}`));

  // 8) Marqueur de boot + environnement.
  const s = envSnapshot();
  pushLog("info", "boot", `BOOT origin=${s.origin} native=${s.isNative} platform=${s.platform} online=${s.online} sw=${s.swControlled} cookies=${s.cookiesPresent}`);
  pushLog("info", "boot", `UA=${s.userAgent}`);
}

export { LS_KEY };
